import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import api from '@/utils/api';
import { toast } from 'sonner';
import { ArrowLeft, ShieldCheck } from 'lucide-react';

const POSITIONS = [
  'Loosehead Prop', 'Hooker', 'Tighthead Prop', 'Lock (4)', 'Lock (5)',
  'Blindside Flanker', 'Openside Flanker', 'Number 8', 'Scrum-half',
  'Fly-half', 'Left Wing', 'Inside Centre', 'Outside Centre', 'Right Wing',
  'Fullback', 'Utility Back', 'Utility Forward',
];

const ageFromDob = (dob) => {
  if (!dob) return null;
  const d = new Date(dob);
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  if (now.getMonth() < d.getMonth() || (now.getMonth() === d.getMonth() && now.getDate() < d.getDate())) age -= 1;
  return age;
};

export default function Auth() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isAdminLogin = searchParams.get('admin') === 'true';
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);

  const [signupData, setSignupData] = useState({
    email: '',
    confirm_email: '',
    password: '',
    full_name: '',
    first_name: '',
    surname: '',
    dob: '',
    role: 'player',
    gender: '',
    terms_accepted: false,
    privacy_accepted: false,
    contact_consent: false,
    parent_guardian_email: '',
    coach_name: '',
    coach_email: '',
    position: '',
    other_positions: []
  });

  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });

  const handleSignup = async (e) => {
    e.preventDefault();
    const isPlayer = signupData.role === 'player';
    const age = ageFromDob(signupData.dob);
    const isU18 = isPlayer && age !== null && age < 18;

    if (isPlayer) {
      if (!signupData.first_name.trim() || !signupData.surname.trim()) { toast.error('First name and surname are required'); return; }
      if (!signupData.dob) { toast.error('Date of birth is required'); return; }
      if (age < 10 || age > 40) { toast.error('Date of birth must be between 10 and 40 years ago'); return; }
      if (signupData.email.trim().toLowerCase() !== signupData.confirm_email.trim().toLowerCase()) {
        toast.error('Email addresses do not match.'); return;
      }
      if (signupData.password.length < 8 || !/\d/.test(signupData.password) || !/[^A-Za-z0-9]/.test(signupData.password)) {
        toast.error('Password must be at least 8 characters with a number and a special character'); return;
      }
      if (isU18 && !signupData.parent_guardian_email) { toast.error('Parent / Guardian email is required for players under 18'); return; }
      if (isU18 && signupData.parent_guardian_email.trim().toLowerCase() === signupData.email.trim().toLowerCase()) {
        toast.error('Parent / Guardian email must differ from your own email'); return;
      }
      if (!signupData.coach_email) { toast.error('Coach email is required'); return; }
      if (!signupData.position) { toast.error('Please select your preferred position'); return; }
      if (!signupData.gender) { toast.error('Please select your gender'); return; }
      if (!signupData.contact_consent) { toast.error('Please tick the contact consent box'); return; }
    }
    if (!signupData.terms_accepted || !signupData.privacy_accepted) {
      toast.error('You must accept the Terms of Service and Privacy Policy');
      return;
    }
    setLoading(true);
    try {
      let payload;
      if (isPlayer) {
        payload = {
          email: signupData.email,
          password: signupData.password,
          full_name: `${signupData.first_name.trim()} ${signupData.surname.trim()}`.trim(),
          first_name: signupData.first_name.trim(),
          surname: signupData.surname.trim(),
          role: 'player',
          gender: signupData.gender,
          dob: signupData.dob,
          is_u18: isU18,
          position: signupData.position,
          other_positions: signupData.other_positions,
          coach_email: signupData.coach_email,
          coach_name: signupData.coach_name,
          contact_consent: true,
          terms_accepted: true,
          privacy_accepted: true,
        };
        if (isU18) {
          payload.parent_guardian_email = signupData.parent_guardian_email;
          payload.guardian_email = signupData.parent_guardian_email;
        }
      } else {
        payload = {
          email: signupData.email,
          password: signupData.password,
          full_name: signupData.full_name,
          role: signupData.role,
          terms_accepted: signupData.terms_accepted,
          privacy_accepted: signupData.privacy_accepted,
        };
      }
      const response = await api.post('/auth/signup', payload);
      if (isPlayer) {
        login(response.data.access_token, response.data.user);
        toast.success('Account created! Complete your full profile and submit it for approval.');
        navigate('/player/dashboard');
      } else {
        toast.success('Account created! Your application is pending approval by an administrator.');
        navigate('/pending');
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.post('/auth/login', loginData);
      login(response.data.access_token, response.data.user);
      toast.success('Login successful!');

      const role = response.data.user.role;
      if (role === 'player') navigate('/player/dashboard');
      else if (role === 'scout') navigate('/scout/dashboard');
      else if (role === 'coach') navigate('/coach/dashboard');
      else if (role === 'school_club') navigate('/school/dashboard');
      else if (role === 'union') navigate('/union/dashboard');
      else if (role === 'admin' || role === 'superadmin') navigate('/admin/dashboard');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  if (isAdminLogin) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-4 hero-gradient" data-testid="admin-auth-page">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <img src="/logo.png" alt="FutureXV Logo" className="h-16 mx-auto mb-4" />
            <div className="flex items-center justify-center gap-2 mb-2">
              <ShieldCheck className="w-6 h-6 text-primary-neon" />
              <h1 className="text-3xl font-heading font-bold text-white">Admin Login</h1>
            </div>
            <p className="text-zinc-400 font-body">FutureXV Platform Administration</p>
          </div>

          <div className="glass-card rounded-2xl p-8 border border-zinc-800">
            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <Label htmlFor="admin-email" className="text-zinc-300">Admin Email</Label>
                <Input
                  id="admin-email"
                  data-testid="admin-login-email-input"
                  type="email"
                  placeholder="info@futurexv.co.za"
                  value={loginData.email}
                  onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                  className="bg-zinc-900/50 border-zinc-800 focus:border-primary-neon focus:ring-1 focus:ring-primary-neon text-white mt-2 h-12"
                  required
                />
              </div>
              <div>
                <Label htmlFor="admin-password" className="text-zinc-300">Password</Label>
                <Input
                  id="admin-password"
                  data-testid="admin-login-password-input"
                  type="password"
                  placeholder="••••••••"
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  className="bg-zinc-900/50 border-zinc-800 focus:border-primary-neon focus:ring-1 focus:ring-primary-neon text-white mt-2 h-12"
                  required
                />
              </div>
              <Button
                type="submit"
                className="w-full btn-primary py-6"
                disabled={loading}
                data-testid="admin-login-submit-button"
              >
                {loading ? 'Signing in...' : 'Sign In as Admin'}
              </Button>
            </form>
          </div>

          <div className="mt-8 text-center">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="text-zinc-400 hover:text-white"
              data-testid="admin-back-to-home-button"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-4 hero-gradient" data-testid="auth-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/logo.png" alt="FutureXV Logo" className="h-16 mx-auto mb-4" />
          <h1 className="text-3xl font-heading font-bold text-white mb-2">Welcome to FutureXV</h1>
          <p className="text-zinc-400 font-body">Discover. Develop. Rise.</p>
        </div>

        <div className="glass-card rounded-2xl p-8 border border-zinc-800">
          <Tabs defaultValue={searchParams.get('mode') === 'signup' ? 'signup' : 'login'} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-8 bg-zinc-900" data-testid="auth-tabs">
              <TabsTrigger value="login" className="data-[state=active]:bg-primary-neon data-[state=active]:text-black" data-testid="login-tab">Login</TabsTrigger>
              <TabsTrigger value="signup" className="data-[state=active]:bg-primary-neon data-[state=active]:text-black" data-testid="signup-tab">Sign Up</TabsTrigger>
            </TabsList>

            <TabsContent value="login" data-testid="login-form">
              <form onSubmit={handleLogin} className="space-y-6">
                <div>
                  <Label htmlFor="login-email" className="text-zinc-300">Email</Label>
                  <Input
                    id="login-email"
                    data-testid="login-email-input"
                    type="email"
                    placeholder="you@example.com"
                    value={loginData.email}
                    onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                    className="bg-zinc-900/50 border-zinc-800 focus:border-primary-neon focus:ring-1 focus:ring-primary-neon text-white mt-2 h-12"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="login-password" className="text-zinc-300">Password</Label>
                  <Input
                    id="login-password"
                    data-testid="login-password-input"
                    type="password"
                    placeholder="••••••••"
                    value={loginData.password}
                    onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                    className="bg-zinc-900/50 border-zinc-800 focus:border-primary-neon focus:ring-1 focus:ring-primary-neon text-white mt-2 h-12"
                    required
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full btn-primary py-6"
                  disabled={loading}
                  data-testid="login-submit-button"
                >
                  {loading ? 'Logging in...' : 'Login'}
                </Button>
                <div className="text-center pt-1">
                  <button
                    type="button"
                    onClick={() => navigate('/forgot-password')}
                    className="text-xs text-zinc-500 hover:text-primary-neon transition-colors"
                    data-testid="forgot-password-link"
                  >
                    Forgot your password?
                  </button>
                </div>
              </form>
            </TabsContent>

            <TabsContent value="signup" data-testid="signup-form">
              <form onSubmit={handleSignup} className="space-y-5">
                <div>
                  <Label htmlFor="signup-role" className="text-zinc-300">I am a...</Label>
                  <Select value={signupData.role} onValueChange={(value) => setSignupData({ ...signupData, role: value, gender: '' })}>
                    <SelectTrigger data-testid="signup-role-select" className="bg-zinc-900/50 border-zinc-800 focus:border-primary-neon focus:ring-1 focus:ring-primary-neon text-white mt-2 h-12">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800 text-white">
                      <SelectItem value="player" data-testid="role-option-player">Player</SelectItem>
                      <SelectItem value="coach" data-testid="role-option-coach">Coach</SelectItem>
                      <SelectItem value="school_club" data-testid="role-option-school-club">School / Club</SelectItem>
                      <SelectItem value="scout" data-testid="role-option-scout">Scout / Recruiter</SelectItem>
                      <SelectItem value="union" data-testid="role-option-union">Union / Governing Body</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {signupData.role === 'player' ? (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-zinc-300">First Name</Label>
                        <Input data-testid="signup-first-name-input" value={signupData.first_name} onChange={(e) => setSignupData({ ...signupData, first_name: e.target.value })} maxLength={60}
                          className="bg-zinc-900/50 border-zinc-800 focus:border-primary-neon text-white mt-2 h-12" required />
                      </div>
                      <div>
                        <Label className="text-zinc-300">Surname</Label>
                        <Input data-testid="signup-surname-input" value={signupData.surname} onChange={(e) => setSignupData({ ...signupData, surname: e.target.value })} maxLength={60}
                          className="bg-zinc-900/50 border-zinc-800 focus:border-primary-neon text-white mt-2 h-12" required />
                      </div>
                    </div>
                    <div>
                      <Label className="text-zinc-300">Date of Birth</Label>
                      <Input data-testid="signup-dob-input" type="date" value={signupData.dob} onChange={(e) => setSignupData({ ...signupData, dob: e.target.value })}
                        className="bg-zinc-900/50 border-zinc-800 focus:border-primary-neon text-white mt-2 h-12" required />
                      {ageFromDob(signupData.dob) !== null && ageFromDob(signupData.dob) < 18 && (
                        <p className="text-amber-400 text-xs mt-1.5" data-testid="u18-notice">Under 18 — a parent or guardian email is required, and both your coach and parent/guardian must approve your profile.</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-zinc-300">Email Address</Label>
                      <Input data-testid="signup-email-input" type="email" placeholder="you@example.com" value={signupData.email} onChange={(e) => setSignupData({ ...signupData, email: e.target.value })}
                        className="bg-zinc-900/50 border-zinc-800 focus:border-primary-neon text-white mt-2 h-12" required />
                    </div>
                    <div>
                      <Label className="text-zinc-300">Confirm Email Address</Label>
                      <Input data-testid="signup-confirm-email-input" type="email" placeholder="Re-enter your email" value={signupData.confirm_email} onChange={(e) => setSignupData({ ...signupData, confirm_email: e.target.value })}
                        className="bg-zinc-900/50 border-zinc-800 focus:border-primary-neon text-white mt-2 h-12" required />
                      {signupData.confirm_email && signupData.email.trim().toLowerCase() !== signupData.confirm_email.trim().toLowerCase() && (
                        <p className="text-red-400 text-xs mt-1.5" data-testid="email-mismatch-error">Email addresses do not match.</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-zinc-300">Password</Label>
                      <Input data-testid="signup-password-input" type="password" placeholder="Min 8 chars, 1 number, 1 special character" value={signupData.password} onChange={(e) => setSignupData({ ...signupData, password: e.target.value })}
                        className="bg-zinc-900/50 border-zinc-800 focus:border-primary-neon text-white mt-2 h-12" required />
                    </div>
                    <div>
                      <Label className="text-zinc-300">Gender</Label>
                      <Select value={signupData.gender} onValueChange={(value) => setSignupData({ ...signupData, gender: value })}>
                        <SelectTrigger data-testid="signup-gender-select" className="bg-zinc-900/50 border-zinc-800 focus:border-primary-neon text-white mt-2 h-12">
                          <SelectValue placeholder="Select gender" />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800 text-white">
                          <SelectItem value="male" data-testid="gender-option-male">Male</SelectItem>
                          <SelectItem value="female" data-testid="gender-option-female">Female</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-zinc-300">Preferred Position</Label>
                      <Select value={signupData.position} onValueChange={(value) => setSignupData({ ...signupData, position: value })}>
                        <SelectTrigger data-testid="signup-position-select" className="bg-zinc-900/50 border-zinc-800 focus:border-primary-neon text-white mt-2 h-12">
                          <SelectValue placeholder="Select position" />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800 text-white max-h-64">
                          {POSITIONS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-zinc-300">Other Positions Played <span className="text-zinc-500">(optional)</span></Label>
                      <div className="flex flex-wrap gap-1.5 mt-2" data-testid="signup-other-positions">
                        {POSITIONS.filter((p) => p !== signupData.position).map((p) => (
                          <button key={p} type="button"
                            onClick={() => setSignupData((prev) => ({ ...prev, other_positions: prev.other_positions.includes(p) ? prev.other_positions.filter((x) => x !== p) : [...prev.other_positions, p] }))}
                            className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${signupData.other_positions.includes(p) ? 'bg-primary-neon text-black border-primary-neon' : 'bg-zinc-900 text-zinc-400 border-zinc-700 hover:border-zinc-500'}`}>
                            {p}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label className="text-zinc-300">Coach Email</Label>
                      <Input data-testid="signup-coach-email-input" type="email" placeholder="coach@school.co.za" value={signupData.coach_email} onChange={(e) => setSignupData({ ...signupData, coach_email: e.target.value })}
                        className="bg-zinc-900/50 border-zinc-800 focus:border-primary-neon text-white mt-2 h-12" required />
                      <p className="text-zinc-500 text-xs mt-1.5">Your coach will receive an approval request when you submit your full profile.</p>
                    </div>
                    <div>
                      <Label className="text-zinc-300">Coach Name <span className="text-zinc-500">(optional)</span></Label>
                      <Input data-testid="signup-coach-name-input" value={signupData.coach_name} onChange={(e) => setSignupData({ ...signupData, coach_name: e.target.value })}
                        className="bg-zinc-900/50 border-zinc-800 focus:border-primary-neon text-white mt-2 h-12" />
                    </div>
                    <div>
                      <Label htmlFor="parent-email" className="text-zinc-300">
                        Parent / Guardian Email {ageFromDob(signupData.dob) !== null && ageFromDob(signupData.dob) < 18 ? <span className="text-amber-400">(required — under 18)</span> : <span className="text-zinc-500">(if under 18)</span>}
                      </Label>
                      <Input id="parent-email" data-testid="signup-parent-email-input" type="email" placeholder="parent@email.com" value={signupData.parent_guardian_email} onChange={(e) => setSignupData({ ...signupData, parent_guardian_email: e.target.value })}
                        className="bg-zinc-900/50 border-zinc-800 focus:border-primary-neon text-white mt-2 h-12"
                        required={ageFromDob(signupData.dob) !== null && ageFromDob(signupData.dob) < 18} />
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <Label htmlFor="signup-name" className="text-zinc-300">Full Name</Label>
                      <Input id="signup-name" data-testid="signup-name-input" type="text" placeholder="John Doe" value={signupData.full_name} onChange={(e) => setSignupData({ ...signupData, full_name: e.target.value })}
                        className="bg-zinc-900/50 border-zinc-800 focus:border-primary-neon text-white mt-2 h-12" required />
                    </div>
                    <div>
                      <Label htmlFor="signup-email" className="text-zinc-300">Email</Label>
                      <Input id="signup-email" data-testid="signup-email-input" type="email" placeholder="you@example.com" value={signupData.email} onChange={(e) => setSignupData({ ...signupData, email: e.target.value })}
                        className="bg-zinc-900/50 border-zinc-800 focus:border-primary-neon text-white mt-2 h-12" required />
                    </div>
                    <div>
                      <Label htmlFor="signup-password" className="text-zinc-300">Password</Label>
                      <Input id="signup-password" data-testid="signup-password-input" type="password" placeholder="••••••••" value={signupData.password} onChange={(e) => setSignupData({ ...signupData, password: e.target.value })}
                        className="bg-zinc-900/50 border-zinc-800 focus:border-primary-neon text-white mt-2 h-12" required />
                    </div>
                  </>
                )}
                <div className="space-y-3 pt-2">
                  {signupData.role === 'player' && (
                    <label className="flex items-start gap-3 cursor-pointer" data-testid="contact-consent-checkbox-label">
                      <input type="checkbox" checked={signupData.contact_consent} onChange={(e) => setSignupData({ ...signupData, contact_consent: e.target.checked })}
                        className="mt-1 w-4 h-4 rounded border-zinc-600 bg-zinc-900 text-primary-neon focus:ring-primary-neon" data-testid="contact-consent-checkbox" />
                      <span className="text-zinc-400 text-sm">I agree that FutureXV may store my contact details and send me account, consent, and notification emails.</span>
                    </label>
                  )}
                  <label className="flex items-start gap-3 cursor-pointer" data-testid="terms-checkbox-label">
                    <input type="checkbox" checked={signupData.terms_accepted} onChange={(e) => setSignupData({ ...signupData, terms_accepted: e.target.checked })}
                      className="mt-1 w-4 h-4 rounded border-zinc-600 bg-zinc-900 text-primary-neon focus:ring-primary-neon" data-testid="terms-checkbox" />
                    <span className="text-zinc-400 text-sm">I accept the <a href="/terms" target="_blank" className="text-primary-neon underline hover:text-primary-neon/80">Terms of Service</a></span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer" data-testid="privacy-checkbox-label">
                    <input type="checkbox" checked={signupData.privacy_accepted} onChange={(e) => setSignupData({ ...signupData, privacy_accepted: e.target.checked })}
                      className="mt-1 w-4 h-4 rounded border-zinc-600 bg-zinc-900 text-primary-neon focus:ring-primary-neon" data-testid="privacy-checkbox" />
                    <span className="text-zinc-400 text-sm">I accept the <a href="/privacy" target="_blank" className="text-primary-neon underline hover:text-primary-neon/80">Privacy Policy</a> (POPIA compliant)</span>
                  </label>
                </div>
                <Button
                  type="submit"
                  className="w-full btn-primary py-6"
                  disabled={loading}
                  data-testid="signup-submit-button"
                >
                  {loading ? 'Creating account...' : 'Create Account'}
                </Button>
                <p className="text-xs text-zinc-500 text-center mt-2">
                  All applications are reviewed by an administrator before access is granted.
                </p>
              </form>
            </TabsContent>
          </Tabs>
        </div>

        <div className="mt-8 text-center">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-zinc-400 hover:text-white"
            data-testid="back-to-home-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </div>
    </div>
  );
}
